import { EventEmitter } from 'events';
import { BaseEntityType } from '../types';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('ENTITY_EVENT_SERVICE');

export interface EntityEvent {
  type: 'created' | 'updated' | 'deleted';
  entity: BaseEntityType;
  spaceId: string;
  source: 'local' | 'remote' | 'crdt';
  timestamp: number;
  metadata?: {
    windowId?: string;
    command?: string;
    userId?: string;
  };
}

export interface EntityEventListener {
  (event: EntityEvent): void | Promise<void>;
}

class EntityEventService extends EventEmitter {
  constructor() {
    super();
    this.setMaxListeners(50); // Support many listeners across stores
  }

  /**
   * Emit an entity event to all registered listeners
   */
  emitEntityEvent(event: Omit<EntityEvent, 'timestamp'>): void {
    const fullEvent: EntityEvent = {
      ...event,
      timestamp: Date.now()
    };

    log.info(`[EntityEventService] Emitting ${event.type} event:`, {
      entityId: event.entity._id,
      entityName: event.entity.name,
      entityType: event.entity.entityType,
      spaceId: event.spaceId,
      source: event.source,
      metadata: event.metadata
    });

    // Emit specific event type
    this.emit(`entity:${event.type}`, fullEvent);
    
    // Also emit general entity event
    this.emit('entity:*', fullEvent);
  }

  /**
   * Register a listener for specific entity event types
   */
  onEntityEvent(eventType: 'created' | 'updated' | 'deleted' | '*', listener: EntityEventListener): () => void {
    const eventName = `entity:${eventType}`;
    this.on(eventName, listener);
    
    // log.info(`[EntityEventService] Registered listener for ${eventName}`);
    
    // Return unsubscribe function
    return () => {
      // Use removeListener (universal support) instead of off (may not exist in browser polyfills)
      // @ts-ignore - removeListener exists on EventEmitter but TypeScript types may not include it
      this.removeListener(eventName, listener);
      // log.info(`[EntityEventService] Unregistered listener for ${eventName}`);
    };
  }

  /**
   * Convenience method to emit entity created event
   */
  entityCreated(entity: BaseEntityType, spaceId: string, metadata?: EntityEvent['metadata']): void {
    this.emitEntityEvent({
      type: 'created',
      entity,
      spaceId,
      source: 'local',
      metadata
    });
  }

  /**
   * Convenience method to emit entity updated event  
   */
  entityUpdated(entity: BaseEntityType, spaceId: string, metadata?: EntityEvent['metadata']): void {
    this.emitEntityEvent({
      type: 'updated',
      entity,
      spaceId,
      source: 'local',
      metadata
    });
  }

  /**
   * Convenience method to emit entity deleted event
   */
  entityDeleted(entity: BaseEntityType, spaceId: string, metadata?: EntityEvent['metadata']): void {
    this.emitEntityEvent({
      type: 'deleted',
      entity,
      spaceId,
      source: 'local',
      metadata
    });
  }

  /**
   * Get current listener count for debugging
   */
  getListenerCount(): Record<string, number> {
    try {
      // eventNames() may not be available in all EventEmitter implementations
      const events = typeof this.eventNames === 'function' ? (this.eventNames() as string[]) : [];
      const counts: Record<string, number> = {};
      
      events.forEach(eventName => {
        counts[eventName] = this.listenerCount(eventName);
      });
      
      return counts;
    } catch (error) {
      // Fallback if eventNames is not supported
      log.warn('getListenerCount: eventNames() not available', error);
      return {};
    }
  }
}

// Create singleton instance
export const entityEventService = new EntityEventService();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  (window as any).entityEventService = entityEventService;
}
